import { loadAutomationModels } from '../../../automation/migration/LegacyEventsToAst.js';
import { getEffectivePrimaryEntity } from '../../../automation/primaryEntity.js';

function valueText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value.toString === 'function') return value.toString();
  return String(value);
}

function conditionText(model) {
  return (model?.conditions ?? [])
    .map(condition => `${valueText(condition.left)} ${condition.operator ?? '=='} ${valueText(condition.right)}`)
    .join(' && ');
}

function serviceActionLabel(action) {
  if (!action) return 'Not configured';

  const service = action.provider && action.service
    ? `${action.provider}.${action.service}`
    : action.service || action.action || action.provider || action.type;

  return service || 'Not configured';
}

function nestedChooseLabel(action) {
  const branches = action?.choose ?? action?.branches;
  if (!Array.isArray(branches)) return null;

  const branchSummaries = branches.map((branch, index) => {
    const sequence = branch?.sequence ?? branch?.actions ?? [];
    const actions = sequence.map(actionLabel).join(', ') || '—';
    return `branch ${index + 1}: ${actions}`;
  });

  return `choose: ${branchSummaries.join(' | ') || '—'}`;
}

function actionLabel(action) {
  return nestedChooseLabel(action) ?? serviceActionLabel(action);
}

function effectiveBindingEntity(binding, node, configOf) {
  if (!['state', 'mapping', 'switch'].includes(binding?.strategy)) return '';
  return String(binding.entityId ?? '').trim() || getEffectivePrimaryEntity(node, { configOf });
}

function addEventGroup(groups, event) {
  if (!groups.has(event)) {
    groups.set(event, { event, actions: [], chooseBranches: [] });
  }
  return groups.get(event);
}

function pageIdFor(node) {
  let current = node;
  const visited = new Set();
  while (current && !visited.has(current)) {
    visited.add(current);
    if (current.isType?.('page') || current.isType?.('template_page')) return current.haspid;
    current = current.parent ?? current.getParent?.() ?? null;
  }
  return null;
}

/**
 * Build the display-only HASSBuilder summary used by the Designer Outline.
 *
 * This intentionally reads through loadAutomationModels so the Outline has
 * the same current/legacy view as the HASSBuilder itself. It never writes to
 * the node and does not participate in export or schema validation.
 * `configOf(node)` reads a node's configuration; pass getEffectiveHassConfig
 * to include its page's Home Assistant flow graph.
 */
export function buildOutlineHassSummary(node, { configOf = item => item?.hass_config } = {}) {
  const models = loadAutomationModels({
    page_id: pageIdFor(node),
    hasp_id: node?.haspid,
    object: node,
    hass_config: configOf(node) ?? {},
  });

  const stateBindings = models.flatMap(model =>
    (model.bindings ?? []).map((binding, index) => ({
      id: `${model.id}:binding:${binding.id ?? index}`,
      entity: effectiveBindingEntity(binding, node, configOf),
      property: binding.property || '—',
    })),
  );

  const eventGroups = new Map();
  models.forEach((model, modelIndex) => {
    // AutomationEditor treats a model with bindings as a state-binding model;
    // its event/action fields are not part of the HASSBuilder event section.
    if (model.bindings?.length > 0) return;
    const events = model.triggers?.length
      ? model.triggers.map(trigger => trigger.event || '—')
      : [];
    if (events.length === 0 || (model.actions ?? []).length === 0) return;

    events.forEach(event => {
      const group = addEventGroup(eventGroups, event);
      const isChooseBranch = model.conditions?.length > 0 || node?.obj === 'msgbox';
      if (isChooseBranch) {
        const condition = conditionText(model);
        const branchLabel = condition ? `if ${condition}` : `branch ${modelIndex + 1}`;
        group.chooseBranches.push({
          id: `${model.id}:choose:${event}`,
          label: `${branchLabel}: ${(model.actions ?? []).map(actionLabel).join(', ') || '—'}`,
        });
      } else {
        group.actions.push(...(model.actions ?? []).map((action, actionIndex) => ({
          id: `${model.id}:action:${event}:${action.id ?? actionIndex}`,
          label: actionLabel(action),
        })));
      }
    });
  });

  const eventActions = [...eventGroups.values()].map(group => ({
    event: group.event,
    actions: [
      ...group.actions,
      ...(group.chooseBranches.length > 0
        ? [{
            id: `choose:${group.event}`,
            label: `choose: ${group.chooseBranches.map(branch => branch.label).join(' | ')}`,
            isChoose: true,
          }]
        : []),
    ],
  }));

  return { stateBindings, eventActions };
}

